import * as React from 'react';
import { styled } from '@glitz/react';
import { translate } from '@avensia/excite';
import { StatusCheck } from 'webapp/product/StatusCheck';
import { a11yGrey } from 'webapp/shared/sharedTheme';

type StockStatus = 'inStock' | 'lowStock' | 'preOrdered' | 'backOrdered' | 'unknown' | 'outOfStock';
type ProductStatus = 'active' | 'endOfLife' | 'temporarilyOutOfStock' | 'news' | 'blocked';

type Props = {
  variantStatus: NordicNest.Api.ProductStatus;
  stockStatus: NordicNest.Api.StockStatus;
  deliveryTime?: string;
};

export const StockStatus = styled(
  React.memo(({ variantStatus, stockStatus, deliveryTime }: Props) => {
    const message: string[] = [getTranslation(stockStatus, variantStatus)];

    if (deliveryTime && stockStatus !== 'outOfStock' && stockStatus !== 'inStock') {
      message.push(deliveryTime);
    }

    return (
      <Base>
        <Message>
          <StatusCheck stockStatus={stockStatus} variantStatus={variantStatus} />
          <span>{message.join(', ')}</span>
        </Message>
      </Base>
    );
  }),
);

export const getTranslation = (key: NordicNest.Api.StockStatus, variantStatus: NordicNest.Api.ProductStatus) => {
  if (variantStatus === 'blocked') return translate(t => t.productStockStatus.outOfStock);

  switch (variantStatus) {
    case 'active':
      switch (key) {
        case 'inStock':
          return translate(t => t.productStockStatus.inStockActive);
        case 'lowStock':
          return translate(t => t.productStockStatus.lowStockActive);
        case 'preOrdered':
          return translate(t => t.productStockStatus.preOrderedActive);
        case 'backOrdered':
          return translate(t => t.productStockStatus.backOrderedActive);
        case 'outOfStock':
          return translate(t => t.productStockStatus.outOfStockActive);
        case 'unknown':
        default:
          return translate(t => t.productStockStatus.unknownActive);
      }
    case 'endOfLife':
      switch (key) {
        case 'inStock':
          return translate(t => t.productStockStatus.inStockEol);
        case 'lowStock':
          return translate(t => t.productStockStatus.lowStockEol);
        case 'preOrdered':
          return translate(t => t.productStockStatus.preOrderedEol);
        case 'backOrdered':
          return translate(t => t.productStockStatus.discontinuedEol);
        case 'outOfStock':
          return translate(t => t.productStockStatus.discontinuedEol);
        case 'unknown':
        default:
          return translate(t => t.productStockStatus.discontinuedEol);
      }
    case 'temporarilyOutOfStock':
      switch (key) {
        case 'inStock':
          return translate(t => t.productStockStatus.inStockTup);
        case 'lowStock':
          return translate(t => t.productStockStatus.lowStockTup);
        case 'preOrdered':
          return translate(t => t.productStockStatus.preOrderedTup);
        case 'backOrdered':
          return translate(t => t.productStockStatus.backOrderedTup);
        case 'outOfStock':
          return translate(t => t.productStockStatus.outOfStockTup);
        case 'unknown':
        default:
          return translate(t => t.productStockStatus.unknownTup);
      }
    case 'news':
    default:
      switch (key) {
        case 'inStock':
          return translate(t => t.productStockStatus.inStock);
        case 'lowStock':
          return translate(t => t.productStockStatus.lowStock);
        case 'preOrdered':
          return translate(t => t.productStockStatus.preOrdered);
        case 'backOrdered':
          return translate(t => t.productStockStatus.backOrdered);
        case 'outOfStock':
          return translate(t => t.productStockStatus.outOfStock);
        case 'unknown':
        default:
          return translate(t => t.productStockStatus.unknown);
      }
  }
};

const Base = styled.div({
  fontSize: t => t.sharedFontSize.xxs,
  color: a11yGrey,
  display: 'flex',
  alignItems: 'center',
});

const Message = styled.div({
  display: 'flex',
  alignItems: 'center',
  margin: {
    bottom: t => t.sharedFontSize.s,
  },
  minWidth: 0,
  ':last-child': {
    margin: {
      bottom: 0,
    },
  },
});
